import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LayoutService, ScreenSize } from 'libs/portal/src/layout/layout.service';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { NavigationService } from 'libs/portal/src/services/navigation/navigation.service';
import { PaginationService } from 'libs/portal/src/services/pagination/pagination.service';
import { TitleService } from 'libs/portal/src/services/title/title.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { CardTableComponent } from 'libs/portal/src/modules/tools/card-table/components';
import { CardTableAction, CardTableOptions } from 'libs/portal/src/modules/tools/card-table/models';
import { CardTableHeader } from 'libs/portal/src/modules/tools/card-table/models/card-table-header.model';
import { ConfirmationModalComponent } from 'libs/portal/src/modules/home/components/user/components/confirmation-modal/confirmation-modal.component';
import { Endpoints } from '@huge-monorepo/portal/config';
import { User } from 'libs/portal/src/models/User';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { ToastrService } from 'ngx-toastr';
import { Routes } from '@huge-monorepo/portal/config';
import { GenericTableHeaderOptions, RefreshData } from 'libs/portal/src/modules/tools/card-table/models/generic-table-header-options.model';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

export interface UserData {
  id?: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  isOnline: string;
  color: string;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
})
export class UserManagementComponent implements OnInit, OnDestroy {
  @ViewChild('sticky') sticky: TemplateRef<any>;
  @ViewChild('UserTable', { static: false }) public UserTable: CardTableComponent;

  count = 10;
  user: any = null;
  ScreenSize = ScreenSize;
  modalStyle: string = this.layoutService.screenSize > this.ScreenSize.MOBILE_L ? 'auto-height' : '';
  isLoaded = false;

  totalUsers: number;
  loadingUsers = false;
  loadingError = false;
  orderBy = 'Name';
  userTableCount = 10;
  userSearch = '';
  userTableOffset = 0;
  currentUserPage = 1;
  userDesc = true;
  obfuscateDetails = false;
  authSubscription: Subscription;
  userHasWriteAccess: boolean = false;

  tableData: UserData[] = [];
  newData: UserData[] = [];

  options: CardTableOptions = {
    rowId: 'id',
    titleProperty: 'fullName',
    textSize: 'regular',
    subTitleProperty: 'email',
    contentProperty: 'isOnline',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: false,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'selected', 'id', 'firstName', 'lastName', 'accessRevoked', 'userId'],
    stylePropertyPrefix: 'users',
    clickableFlexCard: false,
  };

  headerOptions: GenericTableHeaderOptions = {
    ShowSearch: true,
    SearchPlaceholder: 'Search Users',
    DefaultDesc: true,
    DefaultSort: 'Name',
    EnableAddButton: true,
    SortItems: [
      {
        Name: 'Name',
        Display: 'Name',
      },
      {
        Name: 'Email',
        Display: 'Email',
      },
      {
        Name: 'IsOnline',
        Display: 'Status',
      },
    ],
  };

  headers: CardTableHeader[] = [
    {
      ID: 'fullname',
      Text: 'Name',
      Width: 20,
    },
    {
      ID: 'email',
      Text: 'Email',
      Width: 30,
    },
    {
      ID: 'isOnline',
      Text: 'Status',
      Width: 10,
    },
    {
      ID: 'lastSeen',
      Text: 'Last Seen',
      Width: 20,
    },
  ];

  rowActionsRevoke: CardTableAction[] = [
    {
      title: 'Change',
      color: '',
    },
    {
      title: 'Remove',
      color: '',
    },
    {
      title: 'Revoke Access',
      color: '',
    },
    {
      title: 'Impersonate',
      color: '',
    },
  ];

  rowActionsGrant: CardTableAction[] = [
    {
      title: 'Change',
      color: '',
    },
    {
      title: 'Remove',
      color: '',
    },
    {
      title: 'Grant Access',
      color: '',
    },
    {
      title: 'Impersonate',
      color: '',
    },
  ];

  constructor(
    private titleService: TitleService,
    private navigationService: NavigationService,
    private layoutService: LayoutService,
    private genericService: GenericService,
    private endpoints: Endpoints,
    private paginationService: PaginationService,
    private tools: ToolsService,
    private router: Router,
    private auth: UserRightsService,
    private toastService: ToastrService,
    private routes: Routes,
    private dialog: MatDialog,
  ) { }

  ngAfterViewInit(): void {
    this.layoutService.addStickyHeaderForRoute(this.navigationService.GetCurrentRoute(), this.sticky);
  }

  ngOnDestroy(): void {
    // this.userManagementService.userUpdated.unsubscribe();
    this.authSubscription.unsubscribe();
  }

  ngOnInit() {
    this.titleService.pageTitle = 'User Management';
    this.getUsers(this.orderBy, '', this.userTableCount, this.userTableOffset, this.userDesc);
    this.userHasWriteAccess = this.auth.GetUserPortalReadWrite().PortalWriteAccess;
    this.headerOptions.EnableAddButton = this.userHasWriteAccess;
    this.authSubscription = this.auth.GetUser().subscribe((user) => {
      const prev = this.obfuscateDetails;
      if (user.isObfuscated) {
        this.obfuscateDetails = true;
      } else {
        this.obfuscateDetails = false;
      }
      if (prev != this.obfuscateDetails) {
        this.getUsers(this.orderBy, this.userSearch, this.userTableCount, this.userTableOffset, this.userDesc);
      }
    });
    // this.userManagementService.userUpdated.subscribe(() => this.getUsers(this.orderBy, this.userSearch, this.userTableCount, this.userTableOffset, this.userDesc));
  }

  refreshTable(data: RefreshData): void {
    this.userSearch = data.Search ?? '';
    this.orderBy = data.Sort ?? '';
    this.userDesc = data.Desc ?? this.userDesc;
    this.userTableOffset = 0;
    this.currentUserPage = 1;
    this.getUsers(this.orderBy, this.userSearch, this.userTableCount, this.userTableOffset, this.userDesc);
  }

  setUserTableToRefreshing() {
    setTimeout(() => {
      if (this.UserTable) {
        this.UserTable.StartRefresh();
      }
    });
  }

  setUserTableToFinished() {
    setTimeout(() => {
      if (this.UserTable) {
        this.UserTable.EndRefresh();
      }
    });
  }

  getUsers(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.isLoaded = false;
    this.loadingUsers = true;
    this.setUserTableToRefreshing();
    this.loadingError = false;
    this.genericService.GET(this.endpoints.GET_USERS(count, offset, search, orderBy, desc), true).subscribe(
      (resp: any) => {
        const userData = resp['body'];
        if (userData != null) {
          const tmpData = [];
          userData.forEach((element) => {
            let isOnline = element.OnlineStatus == 'Online';
            let row = {
              id: element.ID,
              userId: element.UserID,
              fullName: this.obfuscateDetails ? this.tools.Obfuscate(element.Name + ' ' + element.Surname, 'User ', 4) : element.Name + ' ' + element.Surname,
              firstName: element.Name,
              lastName: element.Surname,
              email: this.obfuscateDetails ? this.tools.Obfuscate(element.Name + ' ' + element.Surname, 'User', 4, '@hugeconnect.co.za') : element.Email,
              isOnline: element.OnlineStatus,
              accessRevoked: element.AccessRevoked,
              color: element.AccessRevoked ? '#C96D01' : isOnline ? '#3FC944' : '#C93F55',
            };

            if (element.LastSeen) {
              row['lastSeen'] = this.tools.FormatSwedishDateTime(this.tools.ParseAPIDateWithTimezone(element.LastSeen));
            } else {
              row['lastSeen'] = 'Unknown';
            }

            tmpData.push(row);
          });

          this.tableData = tmpData;
        }

        this.totalUsers = this.tools.ExtractPaginationFromHeaders(resp['headers']);

        setTimeout(() => {
          this.loadingUsers = false;
          this.isLoaded = true;
          this.setUserTableToFinished();
          this.paginationService.emit();
        });
      },
      () => {
        this.loadingUsers = false;
        this.setUserTableToFinished();
        this.paginationService.emit();
        this.isLoaded = true;
      },
    );
  }

  getPagedUsers(pageIndex: number) {
    this.userTableOffset = (pageIndex - 1) * this.userTableCount;
    this.currentUserPage = pageIndex;
    this.getUsers(this.orderBy, this.userSearch, this.userTableCount, this.userTableOffset, this.userDesc);
  }

  deleteUser(userData: any) {
    if (userData !== null) {
      this.genericService.DELETE(this.endpoints.DELETE_USER(userData.userId)).subscribe(() => {
        this.getUsers(this.orderBy, '', this.userTableCount, this.userTableOffset, this.userDesc);
      });
    }
  }

  revokeUserAccess(userData: any) {
    if (userData != null) {
      this.genericService.GET(this.endpoints.REVOKE_USER_ACCESS(userData.id)).subscribe(() => {
        this.getUsers(this.orderBy, this.userSearch, this.userTableCount, this.userTableOffset, this.userDesc);
      });
    }
  }

  grantUserAccess(userData: any) {
    if (userData != null) {
      this.genericService.GET(this.endpoints.GRANT_USER_ACCESS(userData.id)).subscribe(() => {
        this.getUsers(this.orderBy, this.userSearch, this.userTableCount, this.userTableOffset, this.userDesc);
      });
    }
  }

  openUserModal(user: any) {
    if (user != null) {
      this.router.navigate([this.routes.UpdateUser(user.id)]);
    } else {
      this.router.navigate([this.routes.AddUser]);
    }
  }

  async openConfirmationModal() {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        confimationMessage: 'Are you sure that you would like to delete this user?',
      },
    });

    let dialogSub = dialogRef.afterClosed().subscribe({
      next: (dataReturned) => {
        if (dataReturned) {
          this.deleteUser(this.user);
        }
        dialogSub.unsubscribe();
      },
    });
  }

  async openRevokeModal() {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        confimationMessage: 'Are you sure that you would like to revoke user access?',
      },
    });

    let dialogSub = dialogRef.afterClosed().subscribe({
      next: (dataReturned) => {
        if (dataReturned.data) {
          this.revokeUserAccess(this.user);
        }
        dialogSub.unsubscribe();
      },
    });
  }

  async openGrantModal() {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        confimationMessage: 'Are you sure that you would like to grant user access?',
      },
    });

    let dialogSub = dialogRef.afterClosed().subscribe({
      next: (dataReturned) => {
        if (dataReturned.data) {
          this.grantUserAccess(this.user);
        }
        dialogSub.unsubscribe();
      },
    });
  }

  refreshData($event: any) {
    setTimeout(() => {
      $event.target.complete();
    }, 2000);
  }

  onAction($event: any) {
    // Use enums for the type of actions

    switch ($event.title) {
      case 'Change':
        this.openUserModal($event.row);
        break;
      case 'Reset':
        this.openUserModal($event.row);
        break;
      case 'Remove':
        this.user = $event.row;
        this.openConfirmationModal();
        break;
      case 'Revoke Access':
        this.user = $event.row;
        this.openRevokeModal();
        break;
      case 'Grant Access':
        this.user = $event.row;
        this.openGrantModal();
        break;
      case 'Impersonate':
        this.ImpersonateUser($event.row);
        break;
    }
  }

  ImpersonateUser(userToImpersonate: User) {
    this.genericService.GET(this.endpoints.IMPERSONATE(userToImpersonate['id'])).subscribe((results) => {
      if (results['body']['impersonatedUser'] != null) {
        // TODO: Change when integrating
        this.auth.StartImpersonation(results['body']['impersonatedUser'], results['body']['impersonatedUser']['UserAccessRoles']);
        this.toastService.success('You are now impersonating user: ' + results['body']['impersonatedUser']['name'], 'Impersonation started', { timeOut: 8 });
      } else {
        this.toastService.error('Impersonation failed. Please contact your system administrator', 'Impersonation failed', { timeOut: 8 });
      }
    });
  }
}
